import { useEffect, useRef } from 'preact/hooks';
import { authFetch } from '../api/authFetch';
import { MarkdownContent } from '../shared-ui/MarkdownContent';
import { CodeViewer } from './CodeViewer';
import { buildArchHash, parseArchHash } from '../utils/archUrl';
import {
  useArchStore,
  useArchGraph,
  useArchPhase,
  useArchCurrent,
  useArchSelectedNodeId,
  useArchSearchQuery,
  useArchCrossQuery,
  useArchSeIndex,
  useArchError,
  useArchFetchNonce,
  graphLoaded,
  graphFailed,
  drillTo,
  drillToSetpoints,
  drillToHarness,
  drillToRunbooks,
  drillToArchDocs,
  drillToGuides,
  goToCrumb,
  selectNode,
  closeNode,
  setSearchQuery,
  runSearch,
  restoreFromHash,
  setSeIndex,
  filterSeIndex,
  parseSelectHref,
  parseArchTarget,
  type ArchNodeWire,
} from '../state/archStore';

// Roadmap tense → glyph (text label carried alongside for a11y — never color/glyph alone, H-absence-5).
const TENSE_GLYPH: Record<string, string> = { now: '▶', next: '◷', done: '▮', unknown: '··' };
/** The roadmap tense pill — rides the existing badge slot; shows glyph + word (text, not color-only). */
function TensePill({ tense }: { tense?: string }) {
  if (!tense) return null;
  return (
    <span class={`arch-row__tense arch-row__tense--${tense}`} title={`roadmap: ${tense}`}>
      <span aria-hidden="true">{TENSE_GLYPH[tense] || '··'}</span> {tense}
    </span>
  );
}

/**
 * Architecture map — see plans/architecture-map-c4.md + plans/arch-map-mobile-redesign.md.
 *
 * Path-routed full page at `/arch`. The wire adapter for `archStore`: fetches GET /arch/graph on every
 * navigation, then renders PER TIER on the server-set `graph.kind`:
 *   - `graph` (structural L0–L2, real edges) → the mermaid diagram via `MarkdownContent`.
 *   - `list`  (Setpoint-Atlas nav tiers: SE index / suites / SEs — flat siblings, NO edges) → a native
 *     vertical list with a sticky filter + compliance-gap badges. Rendering these as a mermaid
 *     flowchart packed disconnected nodes horizontally into an unreadable sliver on mobile — the
 *     category error this fixes.
 */
export function ArchPage() {
  const graph = useArchGraph();
  const phase = useArchPhase();
  const current = useArchCurrent();
  const selectedNodeId = useArchSelectedNodeId();
  const searchQuery = useArchSearchQuery();
  const crossQuery = useArchCrossQuery();
  const seIndex = useArchSeIndex();
  const error = useArchError();
  const fetchNonce = useArchFetchNonce();

  // Hash-reflect bookkeeping (Phase 2b deep-link). `hasEstablishedRef`: the very first successful
  // load this mount REPLACES the current history entry's hash (establishing the anchor) rather than
  // pushing an extra stop; every load after that pushes (so Back walks the visited stack). Cold-load
  // precedence itself (hash wins over the persisted position) is handled in archStore at module scope
  // — this component only needs to reflect + react to CHANGES from here on. `fromPopstateRef`: set by
  // the popstate listener right before it calls `restoreFromHash`; read (and cleared) by the fetch
  // effect below so a popstate-triggered navigation replaces the entry instead of pushing a redundant
  // one on top of the position the user just navigated TO via Back/Forward.
  const hasEstablishedRef = useRef(false);
  const fromPopstateRef = useRef(false);

  // Wire: fetch the graph whenever the navigation position changes (initial load, drill, crumb).
  // The last position survives a reload via the store's `persist` (navigation-position partialize)
  // AND via the `#arch/<level>/<id>` hash (whichever is present wins — see archStore's cold-load seed).
  useEffect(() => {
    let cancelled = false;
    useArchStore.setState({ phase: 'loading', selectedNodeId: null, error: null });
    const qs = `level=${encodeURIComponent(current.level)}&id=${encodeURIComponent(current.id)}`;
    authFetch(`/api/arch/graph?${qs}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((g) => {
        if (cancelled) return;
        graphLoaded(g);
        // Hash-reflect: make this position shareable/bookmarkable. Skip entirely when the browser is
        // already showing this exact hash (e.g. the fetch effect re-firing on a Retry/fetchNonce bump
        // at an unchanged position) — nothing to push or replace.
        const newHash = buildArchHash(g.level, g.id);
        if (window.location.hash !== newHash) {
          const useReplace = !hasEstablishedRef.current || fromPopstateRef.current;
          if (useReplace) window.history.replaceState({ arch: true }, '', newHash);
          else window.history.pushState({ arch: true }, '', newHash);
        }
        hasEstablishedRef.current = true;
        fromPopstateRef.current = false;
      })
      .catch((e) => {
        if (!cancelled) graphFailed(String(e?.message || e));
      });
    return () => {
      cancelled = true;
    };
    // fetchNonce forces a re-fetch on same-position navigation (Retry, re-click ⟐ Setpoints).
  }, [current.level, current.id, fetchNonce]);

  // Popstate (Back/Forward while /arch stays mounted) — react ONLY to `#arch/...` hashes; leave any
  // other hash (`#file:...` — CodeViewer's own popstate listener owns it) untouched. This is the other
  // half of the dual-popstate contract: CodeViewer pushes `#file:` as a CHILD overlay on top of the
  // current `#arch:` entry, so closing it pops back to a hash this listener correctly ignores (no
  // target parses out of it) — CodeViewer's own listener clears its overlay, and whatever `#arch:`
  // position is now showing was already the store's `current` (no refetch needed).
  useEffect(() => {
    const onPop = () => {
      const target = parseArchHash(window.location.hash);
      if (!target) return;
      fromPopstateRef.current = true;
      restoreFromHash(target);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Cross-corpus SE index (Phase 2a search palette) — fetched ONCE into the store and filtered in
  // memory thereafter (no per-keystroke round-trips). Best-effort: a failure just leaves the palette
  // showing zero results rather than blocking the rest of the page.
  useEffect(() => {
    let cancelled = false;
    authFetch('/api/arch/setpoints')
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((data) => {
        if (!cancelled) setSeIndex(Array.isArray(data?.se) ? data.se : []);
      })
      .catch(() => {
        /* best-effort — search palette shows empty results */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // A mermaid node was clicked (GRAPH tier only) → select it (+ open its doc for leaf nodes).
  const handleNodeClick = (href: string) => {
    const nodeId = parseSelectHref(href);
    if (!nodeId || !graph) return;
    const node = graph.nodes.find((n) => n.id === nodeId);
    if (!node) return;
    selectNode(node.id);
    // Open the doc on single-click ONLY for LEAF nodes (no drill affordance). For a drillable node the
    // full-screen CodeViewer overlay would COVER the node sheet's buttons — so we just reveal the sheet.
    const isDrillable = !!node.childTarget || !!node.seTarget;
    // routePath (a cross-link to a shipped full-page route) wins over docPath; docPath is the fallback.
    if (!isDrillable && node.routePath) {
      window.location.href = node.routePath;
    } else if (!isDrillable && node.docPath) {
      window.dispatchEvent(new CustomEvent('open-file-viewer', { detail: { path: node.docPath } }));
    }
  };

  // A LIST row was tapped → drill (childTarget), navigate a cross-link route, or open the doc. No SVG.
  const handleRowTap = (node: ArchNodeWire) => {
    if (node.childTarget) {
      const t = parseArchTarget(node.childTarget);
      if (t) drillTo(t.level, t.id);
    } else if (node.routePath) {
      // Cross-link to a shipped full-page route (e.g. the ↗ /harness control plane). Pathname-switched,
      // not SPA-routed — mirrors how GearMenu/UnifiedDrawer navigate to /arch, /admin, /guide.
      window.location.href = node.routePath;
    } else if (node.docPath) {
      window.dispatchEvent(new CustomEvent('open-file-viewer', { detail: { path: node.docPath } }));
    }
  };

  const selectedNode = graph?.nodes.find((n) => n.id === selectedNodeId) || null;
  const drillTarget = selectedNode?.childTarget ? parseArchTarget(selectedNode.childTarget) : null;
  const seDrill = selectedNode?.seTarget ? parseArchTarget(selectedNode.seTarget) : null;
  const rmDrill = selectedNode?.roadmapTarget ? parseArchTarget(selectedNode.roadmapTarget) : null;
  const rbDrill = selectedNode?.rbTarget ? parseArchTarget(selectedNode.rbTarget) : null;
  const adDrill = selectedNode?.adTarget ? parseArchTarget(selectedNode.adTarget) : null;
  const hgDrill = selectedNode?.hgTarget ? parseArchTarget(selectedNode.hgTarget) : null;
  // Renderer tier. Prefer the server `kind`; fall back to the edgeless levels (`SE`/`RM`) when it's
  // absent so a frontend deployed AHEAD of the backend (or hitting a cached/older response) still
  // renders these flat-sibling tiers as native lists rather than the old horizontal sliver.
  const isRMLevel = graph?.level === 'RM';
  const isHNLevel = graph?.level === 'HN';
  // RB has TWO list tiers (index bucket rows + per-project leaf rows) — only the LEAF tier (a
  // non-empty id) needs the dedicated row renderer (badges + inline steps); the index tier is a
  // plain drillable list of buckets and falls through to the generic `arch-row` branch below,
  // mirroring how the SE index tier reuses that same generic branch.
  const isRBLeafLevel = graph?.level === 'RB' && graph.id !== '';
  // AD/HG both have only ONE list tier that needs badge rendering (the LEAF tier — provenance /
  // audience); their index tiers are plain drillable bucket lists that fall through to the generic
  // `arch-row` branch, mirroring the RB index/leaf split above.
  const isADLeafLevel = graph?.level === 'AD' && graph.id !== '';
  const isHGLeafLevel = graph?.level === 'HG' && graph.id !== '';
  const isList = graph ? (graph.kind ? graph.kind === 'list' : graph.level === 'SE' || isRMLevel || isHNLevel) : false;
  // DATA/CODE legend arms only when a CODE-tier binding exists (all-DATA seed today → hidden; F-002 superseded).
  const hnAnyCode = isHNLevel && !!graph?.nodes.some((n) => n.harnessTier === 'CODE');
  const mermaidText = graph ? '```mermaid\n' + graph.mermaid + '\n```' : '';

  // Tier-2 list filtering (client-side over the current graph's nodes).
  const q = searchQuery.trim().toLowerCase();
  const listNodes = graph?.nodes || [];
  const filtered = q ? listNodes.filter((n) => n.label.toLowerCase().includes(q)) : listNodes;

  return (
    <div class="arch-page" style="display:flex;flex-direction:column;height:100vh;overflow:hidden;">
      {/* Header + breadcrumb */}
      <header style="padding:10px 14px;border-bottom:1px solid var(--border,#2a2a3a);flex-shrink:0;">
        <div style="display:flex;align-items:center;justify-content:space-between;gap:8px;margin-bottom:4px;">
          <span style="font-weight:600;font-size:15px;">🗺️ Architecture Map</span>
          <div style="display:flex;gap:6px;flex-shrink:0;">
            <button
              type="button"
              onClick={() => runSearch(crossQuery === null ? '' : null)}
              title="Search across every setpoint name and suite name in the workspace (485+ SEs, cross-project — not just the current tier)"
              aria-pressed={crossQuery !== null}
              style={`background:${crossQuery !== null ? 'var(--accent,#7aa2f7)' : 'none'};border:1px solid var(--accent,#7aa2f7);color:${crossQuery !== null ? '#fff' : 'var(--accent,#7aa2f7)'};border-radius:6px;padding:4px 10px;font-size:12px;cursor:pointer;flex-shrink:0;min-height:32px;`}
            >
              🔍 Search
            </button>
            <button
              type="button"
              onClick={() => drillToSetpoints()}
              title="Setpoint Atlas — every behavioral contract (Setpoint Eval) in the workspace"
              style="background:none;border:1px solid var(--accent,#7aa2f7);color:var(--accent,#7aa2f7);border-radius:6px;padding:4px 10px;font-size:12px;cursor:pointer;flex-shrink:0;min-height:32px;"
            >
              ⟐ Setpoints
            </button>
            <button
              type="button"
              onClick={() => drillToHarness()}
              title="AKB / Harness — the role→model bindings that govern the agent (read-only view of the harness control plane; ↗ links to the /harness editor)"
              style="background:none;border:1px solid var(--accent,#7aa2f7);color:var(--accent,#7aa2f7);border-radius:6px;padding:4px 10px;font-size:12px;cursor:pointer;flex-shrink:0;min-height:32px;"
            >
              ⚙ Harness
            </button>
            <button
              type="button"
              onClick={() => drillToRunbooks()}
              title="Runbooks — every procedural runbook in the workspace (registry/runbooks.yaml), grouped per project"
              style="background:none;border:1px solid var(--accent,#7aa2f7);color:var(--accent,#7aa2f7);border-radius:6px;padding:4px 10px;font-size:12px;cursor:pointer;flex-shrink:0;min-height:32px;"
            >
              📖 Runbooks
            </button>
            <button
              type="button"
              onClick={() => drillToArchDocs()}
              title="Architecture Docs — every authored C4/architecture doc in the workspace (docs/architecture/*.md), grouped per project"
              style="background:none;border:1px solid var(--accent,#7aa2f7);color:var(--accent,#7aa2f7);border-radius:6px;padding:4px 10px;font-size:12px;cursor:pointer;flex-shrink:0;min-height:32px;"
            >
              🏛 Arch Docs
            </button>
            <button
              type="button"
              onClick={() => drillToGuides()}
              title="Guides — every human-facing guide/manual in the workspace (docs/guides/*.md), grouped per project"
              style="background:none;border:1px solid var(--accent,#7aa2f7);color:var(--accent,#7aa2f7);border-radius:6px;padding:4px 10px;font-size:12px;cursor:pointer;flex-shrink:0;min-height:32px;"
            >
              📘 Guides
            </button>
          </div>
        </div>
        <nav style="display:flex;flex-wrap:wrap;gap:4px;align-items:center;font-size:13px;">
          {(graph?.breadcrumb || [{ level: 'L0', id: '', label: 'Workspace' }]).map((c, i, arr) => {
            const isLast = i === arr.length - 1;
            return (
              <span key={`${c.level}:${c.id}`} style="display:inline-flex;align-items:center;gap:4px;">
                {i > 0 && <span style="opacity:0.5;">›</span>}
                {isLast ? (
                  <span style="font-weight:600;">{c.label}</span>
                ) : (
                  <button
                    type="button"
                    onClick={() => goToCrumb(c)}
                    style="background:none;border:none;color:var(--accent,#7aa2f7);cursor:pointer;padding:0;font-size:13px;text-decoration:underline;"
                  >
                    {c.label}
                  </button>
                )}
              </span>
            );
          })}
        </nav>
      </header>

      {/* Content area — render-switch on graph.kind */}
      <main class="arch-main" style="flex:1;overflow:auto;position:relative;">
        {/* Cross-corpus search palette (Phase 2a) — an overlay mode, independent of `phase`/`graph`;
            it reads its own `seIndex` (fetched once) rather than the current tier's nodes. */}
        {crossQuery !== null && (
          <div class="arch-search">
            <div class="arch-search__bar">
              <input
                type="text"
                class="arch-filter"
                inputMode="search"
                aria-label="Search all setpoints and suites"
                autoFocus
                placeholder={seIndex ? `Search ${seIndex.length} setpoints…` : 'Loading search index…'}
                value={crossQuery}
                onInput={(e) => runSearch((e.target as HTMLInputElement).value)}
              />
              <button type="button" class="arch-filter__clear" aria-label="Close search" onClick={() => runSearch(null)}>
                ✕
              </button>
            </div>
            {(() => {
              const hasQuery = crossQuery.trim().length > 0;
              if (!hasQuery) {
                return (
                  <div style="padding:14px;opacity:0.7;">
                    {seIndex ? `Type to search across ${seIndex.length} setpoints…` : 'Loading search index…'}
                  </div>
                );
              }
              const result = filterSeIndex(seIndex ?? [], crossQuery);
              if (result.suites.length === 0 && result.ses.length === 0) {
                return <div style="padding:14px;opacity:0.7;">No matches for “{crossQuery}”.</div>;
              }
              return (
                <>
                  {result.suites.length > 0 && (
                    <>
                      <div class="arch-search__section">SUITES ({result.suites.length})</div>
                      <ul class="arch-rows" role="list">
                        {result.suites.map((s) => (
                          <li key={`suite:${s.project}/${s.suite}`} role="listitem" class="arch-rowwrap">
                            <button
                              type="button"
                              class="arch-row"
                              onClick={() => {
                                const t = parseArchTarget(s.seTarget);
                                runSearch(null);
                                if (t) drillTo(t.level, t.id);
                              }}
                            >
                              <span class="arch-row__label">{s.suite}</span>
                              <span class="arch-row__badge" title="project · matching setpoints">
                                {s.project} · {s.count}
                              </span>
                              <span class="arch-row__chevron" aria-hidden="true">›</span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                  {result.ses.length > 0 && (
                    <>
                      <div class="arch-search__section">SETPOINTS ({result.ses.length})</div>
                      <ul class="arch-rows" role="list">
                        {result.ses.map((se) => (
                          <li key={`se:${se.project}/${se.suite}/${se.name}`} role="listitem" class="arch-rowwrap">
                            <button
                              type="button"
                              class="arch-row"
                              onClick={() => {
                                runSearch(null);
                                if (se.path) {
                                  window.dispatchEvent(new CustomEvent('open-file-viewer', { detail: { path: se.path } }));
                                } else {
                                  const t = parseArchTarget(se.seTarget);
                                  if (t) drillTo(t.level, t.id);
                                }
                              }}
                            >
                              <span class="arch-row__label">{se.name}</span>
                              {!se.hasMermaid && (
                                <span class="arch-row__badge" title="This SE has no README mermaid (no GIVEN/WHEN/THEN)">
                                  ⚠ missing GWT
                                </span>
                              )}
                              <span class="arch-row__chevron" aria-hidden="true">📄</span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                  <div class="arch-list__count">
                    {result.ses.length} setpoint{result.ses.length === 1 ? '' : 's'} · {result.suites.length} suite
                    {result.suites.length === 1 ? '' : 's'}
                  </div>
                </>
              );
            })()}
          </div>
        )}

        {crossQuery === null && phase === 'loading' && <div style="padding:14px;opacity:0.7;">Loading {current.level}…</div>}
        {crossQuery === null && phase === 'error' && (
          <div style="padding:14px;color:var(--danger,#f7768e);">
            Failed to load {current.level} {current.id}: {error}
            <button type="button" onClick={() => drillTo(current.level, current.id)} style="margin-left:8px;">
              Retry
            </button>
          </div>
        )}

        {/* TIER 2 — native list (flat siblings, no edges). The mobile-first fix. */}
        {crossQuery === null && graph && phase !== 'loading' && isList && (
          <div class="arch-list">
            <div class="arch-list__filter">
              <input
                type="text"
                class="arch-filter"
                inputMode="search"
                aria-label="Filter this list"
                placeholder={`Filter ${listNodes.length} item${listNodes.length === 1 ? '' : 's'}…`}
                value={searchQuery}
                onInput={(e) => setSearchQuery((e.target as HTMLInputElement).value)}
              />
              {searchQuery && (
                <button
                  type="button"
                  class="arch-filter__clear"
                  aria-label="Clear filter"
                  onClick={() => setSearchQuery('')}
                >
                  ✕
                </button>
              )}
            </div>
            <ul class="arch-rows" role="list">
              {filtered.map((n) => {
                const drillable = !!n.childTarget;
                // RM (Program Ledger) tier: a two-line row — tense + label + ring + mentions + 📄 on
                // line 1; the verbatim Status ribbon on line 2 (rows are leaves → tap opens the plan).
                if (isRMLevel) {
                  return (
                    <li key={n.id} role="listitem" class="arch-rowwrap">
                      <button type="button" class="arch-rm-row" onClick={() => handleRowTap(n)}>
                        <div class="arch-rm-row__line1">
                          <TensePill tense={n.roadmapTense} />
                          <span class="arch-rm-row__label">{n.label}</span>
                          {n.roadmapRing ? (
                            <span class="arch-rm-row__ring" title="checklist progress (done / total)">
                              ◔ {n.roadmapRing.done}/{n.roadmapRing.total}
                            </span>
                          ) : null}
                          {n.roadmapMentions ? (
                            <span
                              class="arch-row__badge"
                              title="in-plan PR/issue mentions — plural, non-authoritative (plans cite many historical refs)"
                            >
                              mentions {n.roadmapMentions}
                            </span>
                          ) : null}
                          <span class="arch-rm-row__chevron" aria-hidden="true">
                            📄
                          </span>
                        </div>
                        {/* line 2: verbatim Status ribbon — an ESCAPED JSX text child (S-RENDER); it is
                            untrusted file content, so NEVER dangerouslySetInnerHTML. */}
                        {n.roadmapRibbon ? <div class="arch-rm-row__ribbon">{n.roadmapRibbon}</div> : null}
                        {n.roadmapNext ? <div class="arch-rm-row__next">next: {n.roadmapNext}</div> : null}
                      </button>
                    </li>
                  );
                }
                // HN (AKB-Harness) tier: binding rows are a READ-ONLY readout (display-only div, not a
                // tap target — there is no per-binding drill); the lone cross-link row (no harnessModel)
                // opens the harness-studio plan / the future /harness editor.
                if (isHNLevel) {
                  // Binding rows carry harnessTier; the lone cross-link row does NOT → discriminate on
                  // tier (robust even for a future non-model binding that lacks harnessModel).
                  if (!n.harnessTier) {
                    return (
                      <li key={n.id} role="listitem" class="arch-rowwrap">
                        <button type="button" class="arch-row arch-hn-link" onClick={() => handleRowTap(n)}>
                          <span class="arch-row__label">{n.label}</span>
                          <span class="arch-row__chevron" aria-hidden="true">↗</span>
                        </button>
                      </li>
                    );
                  }
                  return (
                    <li key={n.id} role="listitem" class="arch-rowwrap">
                      <div
                        class="arch-hn-row"
                        title={n.harnessLive ? 'live — wired to a resolveModel call site' : 'latent — seeded default, not yet wired'}
                      >
                        <span class={`arch-hn-row__dot arch-hn-row__dot--${n.harnessLive ? 'live' : 'latent'}`} aria-hidden="true">
                          {n.harnessLive ? '●' : '○'}
                        </span>
                        <span class="arch-hn-row__label">{n.label}</span>
                        {/* model only for kind:'model' bindings (a future hook-script binding has none) */}
                        {n.harnessModel ? <span class="arch-hn-row__model">→ {n.harnessModel}</span> : null}
                        {n.harnessTier ? (
                          <span class={`arch-hn-row__tier arch-hn-row__tier--${n.harnessTier.toLowerCase()}`}>{n.harnessTier}</span>
                        ) : null}
                        {/* live/latent as a TEXT word, never color/dot alone (a11y, H-absence-5) */}
                        <span class="arch-hn-row__state">{n.harnessLive ? 'live' : 'latent'}</span>
                      </div>
                    </li>
                  );
                }
                // RB (Runbooks) LEAF tier: one row per runbook — audience/danger badges + inline
                // steps for a `steps:`-only entry (no separate doc to open). A `missing` reference
                // still renders (no dead click — SE-02) with an explicit warning badge instead of a
                // silently-absent 📄. The index tier (bucket rows) falls through to the generic
                // drillable-row branch below (its count is already baked into the label).
                if (isRBLeafLevel) {
                  return (
                    <li key={n.id} role="listitem" class="arch-rowwrap">
                      <button type="button" class="arch-rb-row" onClick={() => handleRowTap(n)}>
                        <div class="arch-rb-row__line1">
                          <span class="arch-rb-row__label">{n.label}</span>
                          {n.rbAudience ? (
                            <span class="arch-row__badge arch-rb-row__audience" title="who this runbook is for">
                              {n.rbAudience}
                            </span>
                          ) : null}
                          {n.rbDanger && n.rbDanger !== 'low' ? (
                            <span
                              class={`arch-row__badge arch-rb-row__danger arch-rb-row__danger--${n.rbDanger}`}
                              title="danger tier"
                            >
                              ⚠ {n.rbDanger}
                            </span>
                          ) : null}
                          {n.rbMissing ? (
                            <span class="arch-row__badge" title="This runbook's reference: file could not be found on disk">
                              ⚠ missing doc
                            </span>
                          ) : null}
                          <span class="arch-rb-row__chevron" aria-hidden="true">
                            {n.docPath ? '📄' : ''}
                          </span>
                        </div>
                        {n.rbSteps && n.rbSteps.length ? (
                          <ol class="arch-rb-row__steps">
                            {n.rbSteps.map((s, i) => (
                              // S-RENDER: steps are untrusted file content — escaped JSX text child,
                              // never dangerouslySetInnerHTML (mirrors the RM ribbon rule above).
                              <li key={i}>{s}</li>
                            ))}
                          </ol>
                        ) : null}
                      </button>
                    </li>
                  );
                }
                // AD (Architecture Docs) LEAF tier: one row per authored C4/architecture doc — the R7
                // provenance line ("Derived from: …") as a tooltip badge when present (an authored,
                // not-derived doc legitimately has none). Every leaf has a docPath (SE-03) → always 📄.
                if (isADLeafLevel) {
                  return (
                    <li key={n.id} role="listitem" class="arch-rowwrap">
                      <button type="button" class="arch-row" onClick={() => handleRowTap(n)}>
                        <span class="arch-row__label">{n.label}</span>
                        {n.adProvenance ? (
                          <span class="arch-row__badge" title={n.adProvenance}>
                            R7 derived
                          </span>
                        ) : null}
                        <span class="arch-row__chevron" aria-hidden="true">📄</span>
                      </button>
                    </li>
                  );
                }
                // HG (Human Guides) LEAF tier: one row per guide — the audience badge from the 3-line
                // frontmatter (default 'both', SE-02). Every leaf has a docPath (SE-03) → always 📄.
                if (isHGLeafLevel) {
                  return (
                    <li key={n.id} role="listitem" class="arch-rowwrap">
                      <button type="button" class="arch-row" onClick={() => handleRowTap(n)}>
                        <span class="arch-row__label">{n.label}</span>
                        {n.hgAudience ? (
                          <span class="arch-row__badge" title="who this guide is for">
                            {n.hgAudience}
                          </span>
                        ) : null}
                        <span class="arch-row__chevron" aria-hidden="true">📄</span>
                      </button>
                    </li>
                  );
                }
                return (
                  <li key={n.id} role="listitem" class="arch-rowwrap">
                    <button type="button" class="arch-row" onClick={() => handleRowTap(n)}>
                      <span class="arch-row__label">{n.label}</span>
                      {/* Atlas glance pill (SE-index project rows only — roadmapTense is unset elsewhere). */}
                      <TensePill tense={n.roadmapTense} />
                      {n.seGap ? (
                        <span class="arch-row__badge" title="Canonical SEs missing a GIVEN/WHEN/THEN README">
                          ⚠ {n.seGap} gap
                        </span>
                      ) : null}
                      {n.missingMermaid ? (
                        <span class="arch-row__badge" title="This SE has no README mermaid (no GIVEN/WHEN/THEN)">
                          ⚠ missing GWT
                        </span>
                      ) : null}
                      <span class="arch-row__chevron" aria-hidden="true">
                        {drillable ? '›' : n.docPath ? '📄' : ''}
                      </span>
                    </button>
                    {/* A drillable row's primary tap descends; a sibling 📄 keeps its doc (project
                        CLAUDE.md / suite README) reachable — parity with the old graph-node panel. */}
                    {drillable && n.docPath && (
                      <button
                        type="button"
                        class="arch-row__doc"
                        aria-label={`Open doc for ${n.label}`}
                        title="Open this item's doc"
                        onClick={() =>
                          window.dispatchEvent(
                            new CustomEvent('open-file-viewer', { detail: { path: n.docPath } }),
                          )
                        }
                      >
                        📄
                      </button>
                    )}
                  </li>
                );
              })}
              {filtered.length === 0 && (
                <li class="arch-rows__empty">
                  {isRMLevel && listNodes.length === 0
                    ? '— no roadmap yet'
                    : `No items match “${searchQuery}”.`}
                </li>
              )}
            </ul>
            {isHNLevel && (
              <div class="arch-hn-legend">
                <span aria-hidden="true">●</span> live · <span aria-hidden="true">○</span> latent (seeded default, not yet wired)
                {hnAnyCode ? ' · DATA = config-only · CODE = hooks/skills (operator-gated)' : ''}
                <div class="arch-hn-legend__note">source-scan of the harness control plane — read-only (the ↗ row opens the editor)</div>
              </div>
            )}
            <div class="arch-list__count">
              {q ? `${filtered.length} of ${listNodes.length}` : `${listNodes.length} item${listNodes.length === 1 ? '' : 's'}`}
            </div>
          </div>
        )}

        {/* TIER 1 — structural graph (real edges) → the mermaid diagram. */}
        {crossQuery === null && graph && phase !== 'loading' && !isList && (
          <div style="padding:14px;">
            <MarkdownContent text={mermaidText} onDiagramNodeClick={handleNodeClick} />
          </div>
        )}
      </main>

      {/* Node sheet — GRAPH tier only (a mermaid node selection). Pinned to the bottom as a thumb-
          reachable sheet (was a foot-of-100vh aside — the diagnosis-#2 reachability fix). */}
      {crossQuery === null && selectedNode && !isList && (
        <aside class="arch-sheet">
          <div class="arch-sheet__head">
            <span class="arch-sheet__title">{selectedNode.label}</span>
            <button type="button" class="arch-sheet__close" aria-label="Close" onClick={closeNode}>
              ✕
            </button>
          </div>
          <div class="arch-sheet__actions">
            {selectedNode.docPath && (
              <button
                type="button"
                class="arch-sheet__btn"
                title={
                  selectedNode.docPath.endsWith('CLAUDE.md')
                    ? "This node's knowledge corpus — its CLAUDE.md (drill into docs/ via the in-doc links)"
                    : 'Open this node’s doc'
                }
                onClick={() =>
                  window.dispatchEvent(
                    new CustomEvent('open-file-viewer', { detail: { path: selectedNode.docPath } }),
                  )
                }
              >
                {/* AKB-Knowledge bridge: on L0/L1 project nodes docPath IS the CLAUDE.md → frame it as
                    the Knowledge dimension; elsewhere it's a plain doc-open (non-redundant labels). */}
                {selectedNode.docPath.endsWith('CLAUDE.md') ? '🧠 Knowledge · CLAUDE.md + docs' : '📄 Open doc'}
              </button>
            )}
            {drillTarget && (
              <button
                type="button"
                class="arch-sheet__btn"
                onClick={() => drillTarget && drillTo(drillTarget.level, drillTarget.id)}
              >
                ⤵ Drill in
              </button>
            )}
            {seDrill && (
              <button
                type="button"
                class="arch-sheet__btn"
                onClick={() => seDrill && drillTo(seDrill.level, seDrill.id)}
                title="Drill into this project's Setpoint Evals (behavioral contracts)"
              >
                ⟐ {selectedNode.seCount} setpoints
              </button>
            )}
            {rmDrill && (
              <button
                type="button"
                class="arch-sheet__btn"
                onClick={() => rmDrill && drillTo(rmDrill.level, rmDrill.id)}
                title="Drill into this project's roadmap — its plans/ Program Ledger"
              >
                📍 Program{selectedNode.roadmapTense ? ` · ${TENSE_GLYPH[selectedNode.roadmapTense] || ''} ${selectedNode.roadmapTense}` : ''}
              </button>
            )}
            {rbDrill && (
              <button
                type="button"
                class="arch-sheet__btn"
                onClick={() => rbDrill && drillTo(rbDrill.level, rbDrill.id)}
                title="Drill into this project's runbooks (registry/runbooks.yaml procedures)"
              >
                📖 {selectedNode.rbCount} runbook{selectedNode.rbCount === 1 ? '' : 's'}
              </button>
            )}
            {adDrill && (
              <button
                type="button"
                class="arch-sheet__btn"
                onClick={() => adDrill && drillTo(adDrill.level, adDrill.id)}
                title="Drill into this project's authored architecture docs (docs/architecture/*.md)"
              >
                🏛 {selectedNode.adCount} arch doc{selectedNode.adCount === 1 ? '' : 's'}
              </button>
            )}
            {hgDrill && (
              <button
                type="button"
                class="arch-sheet__btn"
                onClick={() => hgDrill && drillTo(hgDrill.level, hgDrill.id)}
                title="Drill into this project's human guides (docs/guides/*.md)"
              >
                📘 {selectedNode.hgCount} guide{selectedNode.hgCount === 1 ? '' : 's'}
              </button>
            )}
          </div>
        </aside>
      )}

      {/* `/arch` is a standalone route (App.tsx) — the shell that normally mounts CodeViewer is NOT
          rendered here. Mount our own so `open-file-viewer` (row tap / 📄 Open doc) has a listener.
          Without this the docs silently never open. SE-10 + arch-setpoint-atlas SE-06 guard click→doc. */}
      <CodeViewer />
    </div>
  );
}
